/*
Copyright (c) 2012-2025 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import { useSelector } from "react-redux"
import { AppState } from "store/store.type"
import { MerchantStoreInfo } from "types/models/menu"
import { getUserStoreID } from "utils/user_helper"
import { useCanViewStoreList } from "order-manager/hooks/useCanViewStoreList"
import useFetchAllStoreList from "./useFetchAllStoreList"

export function refreshOutletsIfNotExist(
  outlets: MerchantStoreInfo[] | undefined,
  isFetchingOutlets: boolean,
  fetchAllStoreList: ReturnType<typeof useFetchAllStoreList>,
) {
  // goddamn class components...
  if (!outlets && !isFetchingOutlets) {
    // refresh outlets in the background if they have not been loaded
    fetchAllStoreList({
      disableGlobalLoading: true,
      stopAfterFirstPage: true,
    })
  }
}

/**
 * to check whether or not a user is a single store user (has only one store)
 * some valid case:
 *  - single store user
 *  - merchant group user (owner, manager) with 1 store
 *  - store cashier role
 */

const useSingleStoreUser = () => {
  const {
    menu: { outlets = [], merchantGroupID, outletsHasMore },
  } = useSelector((state: AppState) => state)

  const canViewStoreList = useCanViewStoreList()

  /**
   * outlets length is 0 means:
   * 1. its a single store user, or
   * 2. its a merchant group user with all store no-active
   */
  // Checking outletsHasMore to avoid the case where the catalog-stores endpoint filters out all non-FOOD/SHOP stores, but there can actually be more stores
  if (outlets?.length === 0 && !outletsHasMore) {
    return {
      isSingleStoreUser: !merchantGroupID || !canViewStoreList,
      storeID: getUserStoreID(),
    }
  }

  // for merchant with 1 store
  // Checking outletsHasMore to avoid the case where the catalog-stores endpoint filters out all non-FOOD/SHOP stores, but there can actually be more stores
  if (outlets?.length === 1 && !outletsHasMore) {
    return {
      isSingleStoreUser: true,
      storeID: outlets[0].merchantID,
    }
  }

  // for merchant with multiple store, check if it dont has access to store list, ex: cashier role
  return {
    isSingleStoreUser: !canViewStoreList,
    storeID: getUserStoreID(),
  }
}

export default useSingleStoreUser
